import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import * as api from "../api";
import ThemedTable from "../components/ThemedTable";
import BidRecordChart from "../components/BidRecordChart";
import AccuracyChart from "../components/AccuracyChart";
import { defaultOptions } from "../gameOptions";
import { getGame, seatLabel, seatsLabel, tableAdjective } from "../games";
import { DEFAULT_LOCATION, DEFAULT_DECK, DEFAULT_FELT } from "../theme";
import "./StatsPage.css";

const percent = (part, whole) => (whole ? `${Math.round((part / whole) * 100)}%` : "—");

function Figure({ label, value, note }) {
  return (
    <li className="stat-figure">
      <b className="serif">{value}</b>
      <span className="stat-figure-label">{label}</span>
      {note && <span className="stat-figure-note">{note}</span>}
    </li>
  );
}

function RecordList({ rows, empty }) {
  if (rows.length === 0) return <p className="stats-empty">{empty}</p>;
  return (
    <ul className="record-list">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="record-opponent">{row.label}</span>
          <span className="record-tally">
            <b>{row.wins}</b>
            <span className="record-dash">–</span>
            <b>{row.losses}</b>
          </span>
        </li>
      ))}
    </ul>
  );
}

// Your record, one tab per size of table. Everything is derived from finished
// games and the rounds behind them, so nothing here can drift out of step with
// the games themselves.
function StatsPage({ gameType = "500" }) {
  const game = getGame(gameType);
  const { session } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(game.modes.includes(4) ? 4 : game.modes[0]);
  const [includeFriendly, setIncludeFriendly] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session) navigate("/", { replace: true, state: { from: game.statsPath } });
  }, [session, navigate, game.statsPath]);

  useEffect(() => {
    if (!session) return;
    let live = true;
    setStats(null);
    setError("");
    api
      .getStats(session.token, mode, includeFriendly, gameType)
      .then((data) => live && setStats(data))
      .catch((err) => live && setError(err.message));
    return () => {
      live = false;
    };
  }, [session, mode, includeFriendly, gameType]);

  if (!session) return null;

  // The naming option is per-table, so a stats page spanning many tables just
  // uses the standard names.
  const options = defaultOptions();

  return (
    <ThemedTable
      locationId={DEFAULT_LOCATION}
      deckId={DEFAULT_DECK}
      feltId={DEFAULT_FELT}
      plain
      scrolling
    >
      <div className="stats-page">
        <header className="stats-header">
          <div>
            <h1 className="serif">Your {game.name} record</h1>
            <p className="stats-subtitle">{session.user.name}</p>
          </div>
          <Link to={game.path} className="btn-ghost stats-back">
            Back to {game.name}
          </Link>
        </header>

        {/* Four first wherever the game offers it, then the rest in order.
            Cancellation Hearts seats eleven, so a game can bring nine tabs
            with it and the words have to give way to the numbers. */}
        <div className="stats-tabs" role="tablist">
          {[...game.modes]
            .sort((a, b) => (b === 4) - (a === 4) || a - b)
            .map((seats) => (
              <button
                key={seats}
                role="tab"
                aria-selected={mode === seats}
                className={`stats-tab${mode === seats ? " on" : ""}`}
                onClick={() => setMode(seats)}
              >
                {game.modes.length > 3 ? seatLabel(seats) : seatsLabel(seats)}
              </button>
            ))}
        </div>

        <label className="stats-friendly-toggle">
          <input
            type="checkbox"
            checked={includeFriendly}
            onChange={(e) => setIncludeFriendly(e.target.checked)}
          />
          Include friendly games
        </label>

        {error && <p className="auth-error">{error}</p>}
        {!stats && !error && <p className="stats-empty">Adding it all up…</p>}

        {stats && (
          <>
            <ul className="stat-figures">
              <Figure
                label="Elo"
                value={stats.elo}
                note={
                  gameType !== "500"
                    ? `${game.name} only, ${seatsLabel(mode).toLowerCase()}`
                    : mode === 4
                    ? "your side against theirs"
                    : "head to head"
                }
              />
              <Figure label="Games" value={stats.games} note={`${stats.wins} won`} />
              <Figure label="Win rate" value={percent(stats.wins, stats.games)} />
              {stats.hearts ? (
                <Figure
                  label="Deals you got away with"
                  value={percent(stats.hearts.clean, stats.hearts.deals)}
                  note={`${stats.hearts.clean} of ${stats.hearts.deals} cost you nothing`}
                />
              ) : stats.euchre ? (
                <Figure
                  label="Hands you called"
                  value={percent(stats.euchre.made, stats.euchre.called)}
                  note={`${stats.euchre.made} of ${stats.euchre.called} came home`}
                />
              ) : (
                <Figure
                  label="Contracts made"
                  value={percent(stats.contracts.made, stats.contracts.total)}
                  note={`${stats.contracts.made} of ${stats.contracts.total} you bought`}
                />
              )}
            </ul>
            <p className="stats-note stats-elo-note">
              Elo starts everyone at 1200 and moves with each finished game — by how
              surprising the result was, and, at a table of four, against the average of the
              two sides. A friendly game — marked that way, or played with a robot at the
              table — never moves it, whatever this checkbox is set to.
              {stats.practiceGames > 0 || stats.practiceRounds > 0 ? (
                <>
                  {" "}
                  You have {stats.practiceGames} friendly game{stats.practiceGames === 1 ? "" : "s"}
                  {stats.practiceRounds > 0 && ` (${stats.practiceRounds} contracts)`}, which{" "}
                  {includeFriendly
                    ? "are folded into the figures below."
                    : "aren't counted in the figures below — tick the box above to fold them in."}
                </>
              ) : (
                " You don't have any friendly games yet."
              )}
            </p>

            {stats.hearts && (
              <>
                <h2 className="home-section overline">What the deals have cost you</h2>
                <ul className="stat-figures">
                  <Figure label="Deals" value={stats.hearts.deals} note="hands played" />
                  <Figure
                    label="Average"
                    value={stats.hearts.averageTaken}
                    note="points a deal"
                  />
                  <Figure label="Worst deal" value={stats.hearts.worst} note="points in one hand" />
                  <Figure
                    label="Moons"
                    value={stats.hearts.moons}
                    note={`${stats.hearts.moonsAgainst} shot at you`}
                  />
                </ul>

                <h2 className="home-section overline">By rule set</h2>
                <div className="stats-panel">
                  <RecordList
                    rows={stats.hearts.variants.map((row) => ({
                      ...row,
                      label: game.rules.variantLabel(row.key),
                    }))}
                    empty={`No finished ${tableAdjective(mode)} games yet.`}
                  />
                </div>
              </>
            )}

            {stats.euchre && (
              <>
                <h2 className="home-section overline">The hands you made trump on</h2>
                <ul className="stat-figures">
                  <Figure label="Called" value={stats.euchre.called} note="hands you bought" />
                  <Figure label="Marches" value={stats.euchre.marches} note="all the tricks" />
                  <Figure
                    label="Lone hands"
                    value={stats.euchre.alone}
                    note={`${stats.euchre.aloneMade} of them made`}
                  />
                  <Figure
                    label="Euchred"
                    value={stats.euchre.called - stats.euchre.made}
                    note="hands you were set on"
                  />
                </ul>

                <h2 className="home-section overline">By rule set</h2>
                <div className="stats-panel">
                  <RecordList
                    rows={stats.euchre.variants.map((row) => ({
                      ...row,
                      label: game.rules.variantLabel(row.key),
                    }))}
                    empty={`No finished ${tableAdjective(mode)} games yet.`}
                  />
                </div>
              </>
            )}

            {gameType === "500" && <>
            <h2 className="home-section overline">How close your contracts came</h2>
            <div className="stats-panel">
              <AccuracyChart
                accuracy={stats.contracts.accuracy}
                total={stats.contracts.numeric}
              />
              {stats.contracts.special > 0 && (
                <p className="stats-note">
                  Not counted here: {stats.contracts.special} no-tricks contract
                  {stats.contracts.special === 1 ? "" : "s"}, of which you made{" "}
                  {stats.contracts.specialMade}. They have no near miss — they&apos;re clean
                  or they&apos;re broken.
                </p>
              )}
            </div>

            <h2 className="home-section overline">Every bid, and how often you made it</h2>
            <div className="stats-panel">
              <BidRecordChart bids={stats.bids} options={options} />
            </div>

            </>}

            {mode === 4 ? (
              <>
                <h2 className="home-section overline">With each partner</h2>
                <div className="stats-panel">
                  <RecordList
                    rows={stats.partners}
                    empty={
                      gameType === "500"
                        ? "No finished games with a partner yet."
                        : "No finished partnership games yet."
                    }
                  />
                </div>

                <h2 className="home-section overline">Each table you&apos;ve sat at</h2>
                <div className="stats-panel">
                  <RecordList
                    rows={stats.tables}
                    empty={`No finished ${tableAdjective(mode)} games yet.`}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 className="home-section overline">Against each opponent</h2>
                <div className="stats-panel">
                  <RecordList
                    rows={stats.tables}
                    empty={`No finished ${tableAdjective(mode)} games yet.`}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ThemedTable>
  );
}

export default StatsPage;
